import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Download, Database, Trash2, RotateCcw, ShieldAlert, RefreshCw } from 'lucide-react';
import { downloadBlob } from '../lib/salespro';
import { toast } from 'sonner';

const COLLECTION_LABELS = {
  users: 'Usuarios', companies: 'Empresas', products: 'Productos',
  product_categories: 'Categorías', clients: 'Clientes', sales: 'Ventas',
  payments: 'Pagos', notifications: 'Notificaciones', over_stock_notices: 'Avisos sobre-stock',
  financial_transactions: 'Transacciones', pricing_actions: 'Cambios de precio',
  payment_methods: 'Métodos de pago', employee_profiles: 'Fichas empleado',
  roles: 'Roles', permissions: 'Permisos', form_templates: 'Plantillas',
};

export default function Deployment() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cleanupConfirm, setCleanupConfirm] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await api.getDataSummary();
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { (async () => { await loadSummary(); })(); }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleBackup = async () => {
    setBusy(true);
    try {
      const res = await api.downloadBackup();
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      downloadBlob(new Blob([res.data], { type: 'application/json' }), match ? match[1] : 'salespro_backup.json');
      toast.success('SalesPRO: Respaldo descargado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al generar respaldo');
    } finally {
      setBusy(false);
    }
  };

  const handleCleanup = async () => {
    setBusy(true);
    try {
      const res = await api.cleanupTestData(cleanupConfirm);
      const total = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`SalesPRO: ${total} registro(s) de prueba eliminados`);
      setCleanupConfirm('');
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al limpiar datos');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('SalesPRO: ¿Seguro? Se borrarán TODAS las ventas, pagos, notificaciones y finanzas. Empresas, usuarios, productos y clientes se conservan.')) return;
    setBusy(true);
    try {
      const res = await api.resetTransactional(resetConfirm);
      const total = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`SalesPRO: ${total} registro(s) transaccionales reiniciados`);
      setResetConfirm('');
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al reiniciar');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="deployment-loading">Cargando...</div>;
  }

  const entries = summary ? Object.entries(summary.summary) : [];
  const totalTest = entries.reduce((a, [, v]) => a + (v.test || 0), 0);

  return (
    <div className="space-y-6" data-testid="deployment-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Despliegue y Datos</h1>
          <p className="text-sm text-[#64748B] mt-1">Respalda, limpia datos de prueba y prepara producción</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadSummary} data-testid="refresh-summary">
          <RefreshCw size={16} className="mr-1.5" /> Actualizar
        </Button>
      </div>

      {/* Resumen de datos */}
      <Card className="border border-[#E2E8F0] shadow-sm" data-testid="data-summary-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
            <Database size={18} className="text-primary" /> Resumen de datos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {entries.map(([col, v]) => (
              <div key={col} className="p-3 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-testid={`summary-${col}`}>
                <p className="text-xs text-[#64748B]">{COLLECTION_LABELS[col] || col}</p>
                <p className="text-2xl font-heading font-bold text-[#022C22]">{v.total}</p>
                {v.test > 0 && <p className="text-[11px] text-orange-600 font-medium">{v.test} de prueba</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Respaldo */}
      <Card className="border border-[#E2E8F0] shadow-sm" data-testid="backup-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
            <Download size={18} className="text-green-600" /> Respaldo completo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#64748B]">Descarga un archivo JSON con todos los datos del sistema (sin contraseñas). Hazlo antes de cualquier limpieza o despliegue a producción.</p>
          <Button onClick={handleBackup} disabled={busy} data-testid="download-backup-button">
            <Download size={16} className="mr-1.5" /> Descargar respaldo (JSON)
          </Button>
        </CardContent>
      </Card>

      {/* Zona peligrosa */}
      <Card className="border-2 border-red-200 shadow-sm" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold flex items-center gap-2 text-red-700">
            <ShieldAlert size={18} /> Zona peligrosa
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Limpiar datos de prueba */}
          <div className="space-y-2">
            <p className="font-semibold text-sm flex items-center gap-2"><Trash2 size={15} className="text-orange-600" /> Limpiar datos de prueba</p>
            <p className="text-xs text-[#64748B]">Elimina los {totalTest} registro(s) marcados con prefijo <code className="bg-[#F1F5F9] px-1 rounded">TEST_</code>. Las cuentas y datos reales no se tocan.</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Input
                value={cleanupConfirm}
                onChange={(e) => setCleanupConfirm(e.target.value)}
                placeholder="Escribe LIMPIAR para confirmar"
                className="sm:max-w-xs"
                data-testid="cleanup-confirm-input"
              />
              <Button
                variant="outline"
                disabled={busy || cleanupConfirm !== 'LIMPIAR'}
                onClick={handleCleanup}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="cleanup-test-button"
              >
                <Trash2 size={16} className="mr-1.5" /> Limpiar datos de prueba
              </Button>
            </div>
          </div>

          <div className="border-t border-red-100" />

          {/* Reiniciar transaccional */}
          <div className="space-y-2">
            <p className="font-semibold text-sm flex items-center gap-2"><RotateCcw size={15} className="text-red-600" /> Reiniciar datos transaccionales</p>
            <p className="text-xs text-[#64748B]">Borra <b>todas</b> las ventas, pagos, notificaciones, finanzas y reinicia los correlativos a cero. Conserva empresas, usuarios, productos, clientes y configuración. Úsalo justo antes de lanzar a producción.</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="Escribe REINICIAR para confirmar"
                className="sm:max-w-xs"
                data-testid="reset-confirm-input"
              />
              <Button
                disabled={busy || resetConfirm !== 'REINICIAR'}
                onClick={handleReset}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="reset-transactional-button"
              >
                <RotateCcw size={16} className="mr-1.5" /> Reiniciar datos transaccionales
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
